import API from "./API";
import {
  CreateExercisePayload,
  CreateQuestionPayload,
  ExerciseListQuery,
} from "../../Types/Courses";

interface ListParams {
  page?: number;
  per_page?: number;
  search_word?: string;
  educational_system_id?: number;
  level_id?: number;
}

class CourseService extends API {
  static getEducationParams = () => {
    return this.getRequest("education/params", true);
  };

  static getCourses = (params?: ListParams) => {
    const query = new URLSearchParams();
    if (params?.page) query.append("page", params.page.toString());
    if (params?.per_page) query.append("per_page", params.per_page.toString());
    if (params?.search_word) query.append("search_word", params.search_word);
    if (params?.educational_system_id) {
      query.append("educational_system_id", params.educational_system_id.toString());
    }
    if (params?.level_id) query.append("level_id", params.level_id.toString());
    const queryString = query.toString();
    return this.getRequest(
      `manage/courses${queryString ? `?${queryString}` : ""}`,
      true
    );
  };

  static getCourse = (id: number) => this.getRequest(`manage/courses/${id}/show`, true);
  static getCourseAnalytics = (id: number) =>
    this.getRequest(`manage/courses/${id}/analytics`, true);
  static createCourse = (payload: object) =>
    this.postRequest("manage/courses/create", payload, true);
  static updateCourse = (id: number, payload: object) =>
    this.putRequest(`manage/courses/${id}/update`, payload, true);
  static deleteCourse = (id: number) =>
    this.deleteRequest(`manage/courses/${id}/delete`, {}, true);

  static getUnits = (courseId: number) =>
    this.getRequest(`manage/courses/${courseId}/units`, true);
  static createUnit = (courseId: number, payload: object) =>
    this.postRequest(`manage/courses/${courseId}/units/create`, payload, true);
  static updateUnit = (courseId: number, unitId: number, payload: object) =>
    this.putRequest(
      `manage/courses/${courseId}/units/${unitId}/update`,
      payload,
      true
    );
  static deleteUnit = (courseId: number, unitId: number) =>
    this.deleteRequest(`manage/courses/${courseId}/units/${unitId}/delete`, {}, true);
  static reorderUnits = (courseId: number, unitIds: number[]) =>
    this.postRequest(`manage/courses/${courseId}/units/reorder`, { unit_ids: unitIds }, true);

  static getLessons = (courseId: number, unitId: number) =>
    this.getRequest(`manage/courses/${courseId}/units/${unitId}/lessons`, true);
  static createLesson = (courseId: number, unitId: number, payload: object) =>
    this.postRequest(
      `manage/courses/${courseId}/units/${unitId}/lessons/create`,
      payload,
      true
    );
  static updateLesson = (
    courseId: number,
    unitId: number,
    lessonId: number,
    payload: object
  ) =>
    this.putRequest(
      `manage/courses/${courseId}/units/${unitId}/lessons/${lessonId}/update`,
      payload,
      true
    );
  static deleteLesson = (courseId: number, unitId: number, lessonId: number) =>
    this.deleteRequest(
      `manage/courses/${courseId}/units/${unitId}/lessons/${lessonId}/delete`,
      {},
      true
    );
  static reorderLessons = (courseId: number, unitId: number, lessonIds: number[]) =>
    this.postRequest(
      `manage/courses/${courseId}/units/${unitId}/lessons/reorder`,
      { lesson_ids: lessonIds },
      true
    );

  static getExercises = (params?: ExerciseListQuery) => {
    const query = new URLSearchParams();
    if (params?.page) query.append("page", params.page.toString());
    if (params?.per_page) query.append("per_page", params.per_page.toString());
    if (params?.search_word) query.append("search_word", params.search_word);
    const queryString = query.toString();
    return this.getRequest(
      `manage/exercises${queryString ? `?${queryString}` : ""}`,
      true
    );
  };

  static getExercise = (id: number) =>
    this.getRequest(`manage/exercises/${id}/show`, true);
  static getExerciseAnalytics = (id: number) =>
    this.getRequest(`manage/exercises/${id}/analytics`, true);
  static createExercise = (payload: CreateExercisePayload) =>
    this.postRequest("manage/exercises/create", payload, true);
  static updateExercise = (id: number, payload: object) =>
    this.putRequest(`manage/exercises/${id}/update`, payload, true);
  static deleteExercise = (id: number) =>
    this.deleteRequest(`manage/exercises/${id}/delete`, {}, true);

  static getQuestions = (exerciseId: number) =>
    this.getRequest(`manage/exercises/${exerciseId}/questions`, true);
  static createQuestion = (exerciseId: number, payload: CreateQuestionPayload) =>
    this.postRequest(
      `manage/exercises/${exerciseId}/questions/create`,
      payload,
      true
    );
  static updateQuestion = (exerciseId: number, questionId: number, payload: object) =>
    this.putRequest(
      `manage/exercises/${exerciseId}/questions/${questionId}/update`,
      payload,
      true
    );
  static deleteQuestion = (exerciseId: number, questionId: number) =>
    this.deleteRequest(
      `manage/exercises/${exerciseId}/questions/${questionId}/delete`,
      {},
      true
    );
  static reorderQuestions = (exerciseId: number, questionIds: number[]) =>
    this.postRequest(
      `manage/exercises/${exerciseId}/questions/reorder`,
      { question_ids: questionIds },
      true
    );
}

export default CourseService;
